import React from "react";
import {observer} from "mobx-react-lite";
import GameStore from "../../store/game";
import BonusStore from "../../store/bonus";
import {WordContourHintPrice, TimeBoostHintPrice} from "../../model/prices";

const hintsConfig = {
    time_boost: {
        title: "Добавить время",
        description: "+20 секунд к таймеру",
    },
    word_contour: {
        title: "Показать контур",
        description: "Показать контур одного слова",
    },
};

const hintPrices = {
    time_boost: TimeBoostHintPrice,
    word_contour: WordContourHintPrice,
};

// Панель подсказок с балансами и лимитами
export const HintPanel = observer(({onUseHint, onBuyHint, onNeedCoins}) => {
    const wallet = GameStore.hintWallet || {};
    const limits = GameStore.hintLimits || {};
    const isGameActive = GameStore.isActive;
    const playerBalances = GameStore.playerHintBalances || {};
    const isPlayerHintBalancesLoaded = GameStore.isPlayerHintBalancesLoaded;

    const getGameRemaining = (type) => {
        const limitObj = limits[type] || {};
        const maxLimit = Number(limitObj?.max ?? limitObj?.total ?? limitObj?.limit);
        const usedRaw = Number(limitObj?.used ?? limitObj?.spent);
        if (Number.isFinite(maxLimit)) {
            const used = Number.isFinite(usedRaw) && usedRaw > 0 ? usedRaw : 0;
            const remaining = Math.max(maxLimit - used, 0);
            return {remaining, max: maxLimit, used};
        }

        const available = Number(wallet[type]) || 0;
        const remaining = available < 0 ? 0 : available;
        return {remaining, max: remaining, used: 0};
    };

    const renderHintButton = (type) => {
        const config = hintsConfig[type] || {title: "Подсказка", description: "Доступная опция"};
        const {remaining, max} = getGameRemaining(type);
        const playerBalance = Number(playerBalances[type]) || 0;
        const isUseAvailable = isGameActive && remaining > 0 && playerBalance > 0;
        const badgeColor = playerBalance > 0 ? "arcade-badge" : "inline-flex min-h-[24px] items-center rounded-full border border-ui-disabled-border bg-ui-disabled px-2 py-0.5 text-[11px] font-bold text-ui-disabled-text";
        const limitReached = max !== undefined && Number.isFinite(max) && remaining === 0 && max > 0;
        const buyCost = Number(hintPrices[type] ?? 0);
        const canBuyAndUse = isPlayerHintBalancesLoaded
            && isGameActive
            && remaining > 0
            && playerBalance === 0
            && typeof onBuyHint === "function"
            && typeof onUseHint === "function"
            && buyCost > 0;
        const hasEnoughCoins = BonusStore.coins >= buyCost;
        const canAffordBuy = canBuyAndUse && hasEnoughCoins;
        // Покупаем подсказку и применяем её сразу после успешной покупки.
        const handleBuyAndUse = () => {
            if (!canAffordBuy) {
                return;
            }
            const result = onBuyHint?.(type, {skipBalanceUpdate: true});
            if (result && typeof result.then === "function") {
                // Вызываем использование только после успешной покупки.
                result.then(() => onUseHint?.(type)).catch(() => {});
                return;
            }
            onUseHint?.(type);
        };
        const handleUse = () => {
            if (isUseAvailable) {
                onUseHint?.(type);
                return;
            }
            if (canBuyAndUse) {
                handleBuyAndUse();
            }
        };

        return (
            <div key={type} className="arcade-card flex min-h-[140px] flex-col p-3 text-left">
                {/* Заголовок и описание остаются отдельными от кнопки, чтобы действие сразу считывалось. */}
                <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-black text-gray-900">{config.title}</span>
                    <span className={`shrink-0 ${badgeColor}`}>
                        {playerBalance}
                    </span>
                </div>
                <span className="text-xs text-gray-700 mt-1">{config.description}</span>
                <div className="mt-auto pt-3">
                    {isUseAvailable && (
                        <button type="button" className="app-btn-primary min-h-[44px] w-full px-2 py-1 text-xs font-black uppercase" onClick={handleUse}>
                            Использовать
                        </button>
                    )}
                    {canAffordBuy && (
                        <button type="button" className="app-btn-primary min-h-[44px] w-full px-2 py-1 text-xs font-black uppercase" onClick={handleBuyAndUse}>
                            Купить за {buyCost}
                        </button>
                    )}
                    {canBuyAndUse && !hasEnoughCoins && (
                        <button type="button" className="app-btn-secondary min-h-[44px] w-full px-2 py-1 text-xs font-black uppercase" onClick={() => onNeedCoins?.(type)}>
                            Пополнить монеты
                        </button>
                    )}
                    {limitReached && <span className="text-xs font-semibold text-brand-danger">Лимит в игре исчерпан</span>}
                    {!isUseAvailable && !canBuyAndUse && !limitReached && <span className="text-xs text-ui-disabled-text">Подсказка пока недоступна</span>}
                </div>
            </div>
        );
    };

    // Фиксируем порядок подсказок по конфигу, даже если кошелек пришел иначе.
    const hintOrder = Object.keys(hintsConfig);
    const walletTypes = Object.keys(wallet || {}).filter((type) => hintsConfig[type]);
    const hintTypes = walletTypes.length > 0
        ? hintOrder.filter((type) => walletTypes.includes(type))
        : hintOrder;

    return (
        <div className="px-4 grid grid-cols-2 gap-2">
            {hintTypes.map(renderHintButton)}
        </div>
    );
});
